import i18n from '../i18n';

/**
 * Nombre del país en el idioma del usuario.
 *
 * El backend manda `countryName` tal cual está en la maestra, que la tiene en
 * ESPAÑOL ("España", "Egipto", "Rumania"...). Para traducirlo hace falta el
 * código ISO, así que se prefiere `countryCode` cuando llega.
 *
 * Mientras no esté desplegada la versión del API que manda el código, se
 * deduce del nombre español, que es lo único disponible. Y si no se reconoce
 * —un país fuera de la lista— se muestra tal cual viene: mejor el nombre en
 * español que un hueco o un código suelto.
 */

// Nombres de la maestra → ISO. Normalizados (sin tildes ni mayúsculas) para no
// depender de cómo estén acentuados en la base.
const NAME_TO_ISO: Record<string, string> = {
  espana: 'ES',
  italia: 'IT',
  argentina: 'AR',
  pakistan: 'PK',
  egipto: 'EG',
  bulgaria: 'BG',
  rumania: 'RO',
  grecia: 'GR',
  'dummy (pruebas)': 'XX',
  dummy: 'XX',
};

function normalize(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function resolveCountryIso(
  countryCode?: string | null,
  countryName?: string | null,
): string | undefined {
  const code = (countryCode ?? '').trim().toUpperCase();
  if (code) return code;
  const name = (countryName ?? '').trim();
  if (!name) return undefined;
  return NAME_TO_ISO[normalize(name)];
}

export function countryLabel(
  countryCode?: string | null,
  countryName?: string | null,
): string | undefined {
  const iso = resolveCountryIso(countryCode, countryName);
  if (iso) {
    const key = `countries.${iso}`;
    const translated = i18n.t(key);
    // i18next devuelve la propia clave cuando no hay traducción: en ese caso
    // no hay nada que ganar y se cae al nombre que mandó el servidor.
    if (translated && translated !== key) return translated;
  }
  const fallback = (countryName ?? '').trim();
  return fallback || undefined;
}
